import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  GeneratedPost,
  TemplateSummary,
  Template,
  TextElement,
  ImageElement,
  ShapeElement,
  IconElement,
  TemplateElement,
  ElementTransform,
  AVAILABLE_FONTS,
  isPastelUri,
  parsePastelUri,
  PREVIEW_FORMATS,
  PreviewFormat,
  CreateGalleryItemRequest,
  GalleryItem,
  AdReview,
  ReviewCapability,
  ReviewMetadata,
  ReviewProposedFix,
} from '@agency-smm/shared';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';
import { reviewApi } from '../services/reviewService';
import { Button } from './Button';
import { FormatSelector } from './FormatSelector';
import { NovahReviewChat } from './NovahReviewChat';
import {
  renderCompositeAtAspectRatio,
} from '../utils/canvasRenderer';
import { enrichIssuesWithFixes, validateProposedFix } from '../utils/reviewAutoFix';
import { usePanBackground, useElementEditing } from '../hooks/previewModal';

// ============== Styled Components ==============

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 200px 1fr 320px;
  height: 90vh;
  max-height: 90vh;
  width: 95vw;
  max-width: 1400px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: 95vh;
    overflow-y: auto;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background};
`;

const CenterPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
  background: #1a1a2e;
`;

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
`;

const CanvasContainer = styled.div<{ $isDragging: boolean }>`
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  user-select: none;
  touch-action: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
`;

const CanvasPreview = styled.canvas`
  max-width: 100%;
  max-height: calc(90vh - 32px);
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const PastelBackground = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ $color }) => $color};
`;

const TemplateSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const OffsetIndicator = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const ModeToggle = styled.div`
  display: flex;
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  width: 100%;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ theme, $active }) => ($active ? 'white' : theme.colors.text)};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.15s ease;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.surfaceHover)};
  }
`;

// Element editing overlay styles
const ElementsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ElementWrapper = styled.div<{
  $x: number;
  $y: number;
  $width: number;
  $height: number;
  $rotation: number;
  $flipH: boolean;
  $flipV: boolean;
  $opacity: number;
  $selected: boolean;
  $zIndex: number;
}>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $width }) => $width}%;
  height: ${({ $height }) => $height}%;
  transform: translate(-50%, -50%) rotate(${({ $rotation }) => $rotation}deg) scale(${({ $flipH }) => ($flipH ? -1 : 1)}, ${({ $flipV }) => ($flipV ? -1 : 1)});
  opacity: ${({ $opacity }) => $opacity};
  z-index: ${({ $zIndex }) => $zIndex};
  cursor: move;
  pointer-events: auto;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : 'transparent')};
  box-sizing: border-box;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}80;
  }
`;

const ResizeHandle = styled.div<{ $position: string }>`
  position: absolute;
  width: 10px;
  height: 10px;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid white;
  border-radius: 2px;
  ${({ $position }) => {
    switch ($position) {
      case 'nw':
        return 'top: -5px; left: -5px; cursor: nw-resize;';
      case 'ne':
        return 'top: -5px; right: -5px; cursor: ne-resize;';
      case 'sw':
        return 'bottom: -5px; left: -5px; cursor: sw-resize;';
      case 'se':
        return 'bottom: -5px; right: -5px; cursor: se-resize;';
      default:
        return '';
    }
  }}
`;

const RotateHandle = styled.div`
  position: absolute;
  top: -32px;
  left: 50%;
  width: 14px;
  height: 14px;
  background: ${({ theme }) => theme.colors.primary};
  border: 2px solid white;
  border-radius: 50%;
  transform: translateX(-50%);
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  &:active { cursor: grabbing; }
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 2px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    transform: translateX(-50%);
  }
`;

const ElementPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.2);
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
`;

const ZoomButton = styled.button`
  width: 28px;
  height: 28px;
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const ZoomLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 40px;
  text-align: center;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 4px 0;
`;

const PanelLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Right Panel styled components
const EditableText = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TagsRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-transform: capitalize;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DynamicTextItem = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 6px 8px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 6px;
`;

const DynamicTextLabel = styled.span`
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-right: 6px;
`;

// ============== Properties panel styled components ==============

const PropFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PropFieldLabel = styled.label`
  font-size: 10px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PropInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const PropTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  font-family: inherit;
  line-height: 1.4;
  resize: vertical;
  white-space: pre-wrap;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const PropSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  cursor: pointer;
`;

const PropColorRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const PropColorSwatch = styled.input`
  width: 36px;
  height: 30px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
`;

const PropTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const PropAlignGroup = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const PropAlignButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 6px;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ theme, $active }) => ($active ? 'white' : theme.colors.text)};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:last-child { border-right: none; }
`;

const PropPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PropElementTypeBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  text-transform: uppercase;
`;

// ============== Types ==============

export type EditMode = 'pan' | 'elements';

export interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: GeneratedPost;
  posts: GeneratedPost[];
  templates: TemplateSummary[];
  workspaceId: string;
  workspaceName: string;
  initialTemplateId?: string;
  previewFormat: PreviewFormat;
  onPreviewFormatChange: (format: PreviewFormat) => void;
  onSaveOffset: (postId: string, offsetX: number, offsetY: number, scale: number) => void;
  onTemplateChange: (postId: string, templateId: string) => void;
  onTemplateCreated?: (template: Template) => void;
  onSaveToGallery?: () => void;
  workspaceLogoUrl?: string;
  logoScale?: number;
  reviewCapability?: ReviewCapability | null;
  onContentSaved?: (postId: string, content: string) => void;
  onNavigateBilling?: () => void;
  brandColors?: string[];
  brandFonts?: string[];
}

// ============== Theme color helpers ==============

const normalizeHex = (c: string | undefined): string | null => {
  if (!c) return null;
  const norm = c.toLowerCase().trim();
  if (!/^#[0-9a-f]{6}$/.test(norm)) return null;
  return norm;
};

const isGrayscale = (hex: string): boolean => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return Math.abs(r - g) < 12 && Math.abs(g - b) < 12 && Math.abs(r - b) < 12;
};

/** Find the most-used non-grayscale hex color across element color fields.
 *  Returns null if no clear accent found. */
function detectAccentColor(elements: TemplateElement[]): string | null {
  const counts = new Map<string, number>();
  const tally = (color: string | undefined) => {
    const norm = normalizeHex(color);
    if (!norm || isGrayscale(norm)) return;
    counts.set(norm, (counts.get(norm) || 0) + 1);
  };
  for (const el of elements) {
    if (el.elementType === 'shape') {
      const s = el as ShapeElement;
      tally(s.backgroundColor);
      tally(s.borderColor);
      tally(s.shadowColor);
      s.gradientColors?.forEach((g) => tally(g.color));
    } else if (el.elementType === 'text') {
      const t = el as TextElement;
      tally(t.textColor);
      tally(t.outlineColor);
    } else if (el.elementType === 'icon') {
      const i = el as IconElement;
      tally(i.iconColor);
    }
  }
  let best: string | null = null;
  let max = 0;
  for (const [color, count] of counts) {
    if (count > max) { max = count; best = color; }
  }
  return best;
}

/** Swap one color for another across all color-bearing fields of every element.
 *  Comparison is case-insensitive; non-hex values (rgba/named) are skipped.
 *  When `onlyFlagged` is true, only elements with `usesBrandColor === true`
 *  are touched (explicit brand-color mode). */
function swapColorInElements(
  elements: TemplateElement[],
  from: string,
  to: string,
  options: { onlyFlagged?: boolean } = {}
): TemplateElement[] {
  const fromNorm = normalizeHex(from);
  if (!fromNorm) return elements;
  const eq = (c: string | undefined) => normalizeHex(c) === fromNorm;
  return elements.map((el) => {
    if (options.onlyFlagged && !el.usesBrandColor) return el;
    if (el.elementType === 'shape') {
      const s = el as ShapeElement;
      const updated: ShapeElement = { ...s };
      if (eq(s.backgroundColor)) updated.backgroundColor = to;
      if (eq(s.borderColor)) updated.borderColor = to;
      if (eq(s.shadowColor)) updated.shadowColor = to;
      if (s.gradientColors) {
        updated.gradientColors = s.gradientColors.map((g) =>
          eq(g.color) ? { ...g, color: to } : g
        );
      }
      return updated as TemplateElement;
    } else if (el.elementType === 'text') {
      const t = el as TextElement;
      const updated: TextElement = { ...t };
      if (eq(t.textColor)) updated.textColor = to;
      if (eq(t.outlineColor)) updated.outlineColor = to;
      return updated as TemplateElement;
    } else if (el.elementType === 'icon') {
      const i = el as IconElement;
      const updated: IconElement = { ...i };
      if (eq(i.iconColor)) updated.iconColor = to;
      return updated as TemplateElement;
    }
    return el;
  });
}

// ============== ElementPropertiesPanel ==============

interface ElementPropertiesPanelProps {
  element: TemplateElement;
  /** Map of dynamic_type → current rendered text (post AI text). Allows the
   * panel to show what's ACTUALLY on the canvas for dynamic elements, instead
   * of the template's static placeholder. */
  dynamicTexts: Record<string, string>;
  /** Template-level brand color (when explicit mode). Used to label the
   * "Uses brand color" toggle so the user knows which color it refers to. */
  brandColor: string | null;
  onUpdate: (patch: Partial<TemplateElement>) => void;
  onUpdateTransform: (patch: Partial<ElementTransform>) => void;
  /** Update the post's per-post AI text for a dynamic element. */
  onUpdateDynamicText: (type: string, value: string) => void;
  /** Regenerate the post's AI text for a dynamic slot via the AI provider.
   *  Returns the new text, which the panel uses to update local state. */
  onRegenerateDynamicText: (type: string) => Promise<string | null>;
  onReorder: (op: 'front' | 'back' | 'forward' | 'backward') => void;
  onDeselect: () => void;
  onDelete: () => void;
}

const FONT_WEIGHTS: TextElement['fontWeight'][] = ['normal', 'bold', '300', '400', '500', '600', '700', '800', '900'];

function ElementPropertiesPanel({
  element,
  dynamicTexts,
  brandColor,
  onUpdate,
  onUpdateTransform,
  onUpdateDynamicText,
  onRegenerateDynamicText,
  onReorder,
  onDeselect,
  onDelete,
}: ElementPropertiesPanelProps) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const isText = element.elementType === 'text';
  const isShape = element.elementType === 'shape';
  const isImage = element.elementType === 'image';
  const text = isText ? (element as TextElement) : null;
  const shape = isShape ? (element as ShapeElement) : null;
  const image = isImage ? (element as ImageElement) : null;

  return (
    <>
      <PropPanelHeader>
        <SectionLabel>Properties</SectionLabel>
        <Button $size="sm" $variant="ghost" onClick={onDeselect}>
          Done
        </Button>
      </PropPanelHeader>

      <PropPanelHeader>
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          {element.name || (isText ? 'Text' : isShape ? 'Shape' : isImage ? 'Image' : 'Element')}
        </span>
        <PropElementTypeBadge>{element.elementType}</PropElementTypeBadge>
      </PropPanelHeader>

      {/* Brand color opt-in toggle */}
      <PropFieldRow>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
          <input
            type="checkbox"
            checked={!!element.usesBrandColor}
            onChange={(e) => onUpdate({ usesBrandColor: e.target.checked } as Partial<TemplateElement>)}
            style={{ cursor: 'pointer' }}
          />
          <span>Uses brand color</span>
          {brandColor && (
            <span
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                borderRadius: 3,
                background: brandColor,
                border: '1px solid #d1d5db',
                marginLeft: 'auto',
              }}
              title={brandColor}
            />
          )}
        </label>
        <span style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.3 }}>
          {brandColor
            ? 'When checked, this element follows the template’s brand color.'
            : 'No brand color set on this template yet — toggle stays off until one is.'}
        </span>
      </PropFieldRow>

      {/* Text-specific */}
      {text && (
        <>
          {text.isDynamic && text.dynamicType ? (
            <>
              <PropFieldRow>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <PropFieldLabel>
                    AI text — {text.dynamicType} (this post)
                  </PropFieldLabel>
                  <button
                    type="button"
                    onClick={async () => {
                      setIsRegenerating(true);
                      try {
                        await onRegenerateDynamicText(text.dynamicType!);
                      } finally {
                        setIsRegenerating(false);
                      }
                    }}
                    disabled={isRegenerating}
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 4,
                      border: '1px solid #6366F1',
                      background: isRegenerating ? '#e0e7ff' : '#6366F1',
                      color: isRegenerating ? '#6366F1' : 'white',
                      cursor: isRegenerating ? 'wait' : 'pointer',
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                    }}
                    title="Regenerate this text with AI"
                  >
                    {isRegenerating ? '… Generating' : '↻ Redo with AI'}
                  </button>
                </div>
                <PropTextarea
                  value={dynamicTexts[text.dynamicType] || ''}
                  onChange={(e) => onUpdateDynamicText(text.dynamicType!, e.target.value)}
                  placeholder={`Edit the ${text.dynamicType} for this post`}
                />
              </PropFieldRow>
              <PropFieldRow>
                <PropFieldLabel>Template fallback (when no AI text)</PropFieldLabel>
                <PropInput
                  type="text"
                  value={text.textContent || ''}
                  onChange={(e) => onUpdate({ textContent: e.target.value } as Partial<TextElement>)}
                />
              </PropFieldRow>
            </>
          ) : (
            <PropFieldRow>
              <PropFieldLabel>Text content</PropFieldLabel>
              <PropTextarea
                value={text.textContent}
                onChange={(e) => onUpdate({ textContent: e.target.value } as Partial<TextElement>)}
              />
              <span style={{ fontSize: 10, color: '#6b7280' }}>
                Press <strong>Enter</strong> for a line break. Bullets work as plain text — use • or - at the start of each line.
              </span>
            </PropFieldRow>
          )}

          <PropFieldRow>
            <PropFieldLabel>Font</PropFieldLabel>
            <PropSelect
              value={text.fontFamily}
              onChange={(e) => onUpdate({ fontFamily: e.target.value } as Partial<TextElement>)}
            >
              {AVAILABLE_FONTS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </PropSelect>
          </PropFieldRow>

          <PropTwoCol>
            <PropFieldRow>
              <PropFieldLabel>Size (px)</PropFieldLabel>
              <PropInput
                type="number"
                min={8}
                max={500}
                value={text.fontSize}
                onChange={(e) => onUpdate({ fontSize: Number(e.target.value) } as Partial<TextElement>)}
              />
            </PropFieldRow>
            <PropFieldRow>
              <PropFieldLabel>Weight</PropFieldLabel>
              <PropSelect
                value={text.fontWeight}
                onChange={(e) => onUpdate({ fontWeight: e.target.value as TextElement['fontWeight'] } as Partial<TextElement>)}
              >
                {FONT_WEIGHTS.map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </PropSelect>
            </PropFieldRow>
          </PropTwoCol>

          <PropFieldRow>
            <PropFieldLabel>Color</PropFieldLabel>
            <PropColorRow>
              <PropColorSwatch
                type="color"
                value={text.textColor || '#ffffff'}
                onChange={(e) => onUpdate({ textColor: e.target.value } as Partial<TextElement>)}
              />
              <PropInput
                type="text"
                value={text.textColor || ''}
                onChange={(e) => onUpdate({ textColor: e.target.value } as Partial<TextElement>)}
              />
            </PropColorRow>
          </PropFieldRow>

          <PropFieldRow>
            <PropFieldLabel>Alignment</PropFieldLabel>
            <PropAlignGroup>
              {(['left', 'center', 'right'] as const).map((align) => (
                <PropAlignButton
                  key={align}
                  $active={text.textAlign === align}
                  onClick={() => onUpdate({ textAlign: align } as Partial<TextElement>)}
                >
                  {align}
                </PropAlignButton>
              ))}
            </PropAlignGroup>
          </PropFieldRow>

          <PropTwoCol>
            <PropFieldRow>
              <PropFieldLabel>Letter spacing</PropFieldLabel>
              <PropInput
                type="number"
                step={0.5}
                value={text.letterSpacing ?? 0}
                onChange={(e) => onUpdate({ letterSpacing: Number(e.target.value) } as Partial<TextElement>)}
              />
            </PropFieldRow>
            <PropFieldRow>
              <PropFieldLabel>Line height</PropFieldLabel>
              <PropInput
                type="number"
                step={0.1}
                min={0.8}
                max={3}
                value={text.lineHeight ?? 1.3}
                onChange={(e) => onUpdate({ lineHeight: Number(e.target.value) } as Partial<TextElement>)}
              />
            </PropFieldRow>
          </PropTwoCol>

          <PropTwoCol>
            <PropFieldRow>
              <PropFieldLabel>Outline color</PropFieldLabel>
              <PropColorSwatch
                type="color"
                value={text.outlineColor || '#000000'}
                onChange={(e) => onUpdate({ outlineColor: e.target.value } as Partial<TextElement>)}
              />
            </PropFieldRow>
            <PropFieldRow>
              <PropFieldLabel>Outline width</PropFieldLabel>
              <PropInput
                type="number"
                min={0}
                max={20}
                value={text.outlineWidth ?? 0}
                onChange={(e) => onUpdate({ outlineWidth: Number(e.target.value) } as Partial<TextElement>)}
              />
            </PropFieldRow>
          </PropTwoCol>
        </>
      )}

      {/* Shape-specific */}
      {shape && (
        <>
          <PropFieldRow>
            <PropFieldLabel>Background color</PropFieldLabel>
            <PropColorRow>
              <PropColorSwatch
                type="color"
                value={(shape.backgroundColor || '#000000').startsWith('#') ? shape.backgroundColor : '#000000'}
                onChange={(e) => onUpdate({ backgroundColor: e.target.value } as Partial<ShapeElement>)}
              />
              <PropInput
                type="text"
                value={shape.backgroundColor || ''}
                onChange={(e) => onUpdate({ backgroundColor: e.target.value } as Partial<ShapeElement>)}
              />
            </PropColorRow>
          </PropFieldRow>

          <PropFieldRow>
            <PropFieldLabel>Border radius (px)</PropFieldLabel>
            <PropInput
              type="number"
              min={0}
              max={500}
              value={shape.borderRadius ?? 0}
              onChange={(e) => onUpdate({ borderRadius: Number(e.target.value) } as Partial<ShapeElement>)}
            />
          </PropFieldRow>
        </>
      )}

      {/* Image-specific */}
      {image && (
        <PropFieldRow>
          <PropFieldLabel>Source</PropFieldLabel>
          <DynamicTextItem>
            {image.assetReferenceType === 'workspace_logo'
              ? 'Workspace logo (resolves per-workspace)'
              : image.imageUrl
                ? image.imageUrl.substring(0, 40) + (image.imageUrl.length > 40 ? '...' : '')
                : 'No source set'}
          </DynamicTextItem>
        </PropFieldRow>
      )}

      {/* Common: opacity */}
      <PropFieldRow>
        <PropFieldLabel>Opacity</PropFieldLabel>
        <PropInput
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={element.opacity ?? 1}
          onChange={(e) => onUpdate({ opacity: Number(e.target.value) })}
        />
      </PropFieldRow>

      {/* Common: position */}
      <SectionLabel>Position</SectionLabel>
      <PropTwoCol>
        <PropFieldRow>
          <PropFieldLabel>X (%)</PropFieldLabel>
          <PropInput
            type="number"
            step={0.5}
            value={element.transform.positionX.toFixed(1)}
            onChange={(e) => onUpdateTransform({ positionX: Number(e.target.value) })}
          />
        </PropFieldRow>
        <PropFieldRow>
          <PropFieldLabel>Y (%)</PropFieldLabel>
          <PropInput
            type="number"
            step={0.5}
            value={element.transform.positionY.toFixed(1)}
            onChange={(e) => onUpdateTransform({ positionY: Number(e.target.value) })}
          />
        </PropFieldRow>
        <PropFieldRow>
          <PropFieldLabel>Width (%)</PropFieldLabel>
          <PropInput
            type="number"
            step={0.5}
            value={element.transform.width.toFixed(1)}
            onChange={(e) => onUpdateTransform({ width: Number(e.target.value) })}
          />
        </PropFieldRow>
        <PropFieldRow>
          <PropFieldLabel>Height (%)</PropFieldLabel>
          <PropInput
            type="number"
            step={0.5}
            value={element.transform.height.toFixed(1)}
            onChange={(e) => onUpdateTransform({ height: Number(e.target.value) })}
          />
        </PropFieldRow>
      </PropTwoCol>

      <PropFieldRow>
        <PropFieldLabel>Rotation (deg)</PropFieldLabel>
        <PropInput
          type="number"
          step={1}
          value={element.transform.rotation}
          onChange={(e) => onUpdateTransform({ rotation: Number(e.target.value) })}
        />
      </PropFieldRow>

      <SectionLabel>Flip</SectionLabel>
      <PropTwoCol>
        <Button
          $size="sm"
          $variant={element.transform.flipHorizontal ? 'primary' : 'outline'}
          onClick={() => onUpdateTransform({ flipHorizontal: !element.transform.flipHorizontal })}
          title="Mirror left↔right"
        >
          ⇆ Horizontal
        </Button>
        <Button
          $size="sm"
          $variant={element.transform.flipVertical ? 'primary' : 'outline'}
          onClick={() => onUpdateTransform({ flipVertical: !element.transform.flipVertical })}
          title="Mirror top↔bottom"
        >
          ⇅ Vertical
        </Button>
      </PropTwoCol>

      <Divider />

      <SectionLabel>Layer order (z: {element.zIndex})</SectionLabel>
      <PropTwoCol>
        <Button $size="sm" $variant="outline" onClick={() => onReorder('front')}>
          ⤒ To Front
        </Button>
        <Button $size="sm" $variant="outline" onClick={() => onReorder('back')}>
          ⤓ To Back
        </Button>
        <Button $size="sm" $variant="outline" onClick={() => onReorder('forward')}>
          ↑ Forward
        </Button>
        <Button $size="sm" $variant="outline" onClick={() => onReorder('backward')}>
          ↓ Backward
        </Button>
      </PropTwoCol>

      <Divider />

      <Button
        $size="sm"
        $variant="outline"
        $fullWidth
        onClick={() => {
          if (window.confirm('Delete this element? You can still cancel by closing without saving.')) {
            onDelete();
          }
        }}
        style={{ color: '#dc2626', borderColor: '#dc2626' }}
      >
        Delete element
      </Button>
    </>
  );
}

// ============== Component ==============

export function PreviewModal({
  isOpen,
  onClose,
  post,
  posts: _posts,  // eslint-disable-line @typescript-eslint/no-unused-vars
  templates,
  workspaceId,
  workspaceName,
  initialTemplateId = '',
  previewFormat,
  onPreviewFormatChange,
  onSaveOffset,
  onTemplateChange,
  onTemplateCreated,
  onSaveToGallery,
  workspaceLogoUrl,
  logoScale,
  reviewCapability,
  onContentSaved,
  onNavigateBilling,
  brandColors,
  brandFonts,
}: PreviewModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  // Template state
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId);
  const [currentTemplate, setCurrentTemplate] = useState<Template | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isRenderingCanvas, setIsRenderingCanvas] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState<EditMode>('pan');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isSavingToGallery, setIsSavingToGallery] = useState(false);
  const [savedToGallery, setSavedToGallery] = useState(false);

  // Theme color state — currently-applied accent color across the template.
  // EXPLICIT mode: template has `brandColor` set in DB. Picker swaps brandColor
  // → user's color but ONLY on elements flagged `usesBrandColor`.
  // HEURISTIC mode: brandColor not set. Detect from elements; swap matching color
  // on every element that has it. Templates created via UI default to heuristic.
  const [themeColor, setThemeColor] = useState<string | null>(null);
  const [originalThemeColor, setOriginalThemeColor] = useState<string | null>(null);
  // True if the template's brand_color column is set (explicit mode).
  const [isExplicitBrand, setIsExplicitBrand] = useState(false);
  // Marks the brand color as having been changed — Save Changes will PUT
  // template.brandColor along with the element edits.
  const [brandColorDirty, setBrandColorDirty] = useState(false);

  // Editable content state
  const [editedContent, setEditedContent] = useState(post.content);
  const [isSavingContent, setIsSavingContent] = useState(false);

  // Editable dynamic texts (per-post overrides for AI-generated text).
  // Mirrors post.dynamicTexts on entry; user edits live here, then on Save
  // Changes we PUT to /posts/:id with the diff.
  const [editedDynamicTexts, setEditedDynamicTexts] = useState<Record<string, string>>(() => ({
    headline: post.dynamicTexts?.headline || '',
    tagline: post.dynamicTexts?.tagline || '',
    cta: post.dynamicTexts?.cta || '',
    quote: post.dynamicTexts?.quote || '',
    stat: post.dynamicTexts?.stat || '',
  }));
  const [hasDynamicTextChanges, setHasDynamicTextChanges] = useState(false);

  // Background pan hook
  const {
    dragOffset, isDragging, hasUnsavedChanges, bgScale,
    handlePanStart, handlePanMove, handlePanEnd,
    handleSavePosition, handleResetOffset,
    handleWheel, handleScaleUp, handleScaleDown, handleScaleReset,
  } = usePanBackground(post.id, post.backgroundOffsetX || 0, post.backgroundOffsetY || 0, post.backgroundScale || 1.0, editMode, onSaveOffset);

  // Element editing hook
  const {
    modifiedElements, setModifiedElements,
    selectedElementId, setSelectedElementId,
    hasElementChanges, setHasElementChanges,
    handleElementMouseDown, handleResizeMouseDown,
    handleRotateMouseDown,
    handleElementMouseMove, handleElementMouseUp,
    resetElementEditing,
  } = useElementEditing(editMode, canvasRef);

  // AI Review state
  const [review, setReview] = useState<AdReview | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Auto-dismiss toast
  useEffect(() => {
    if (toastMessage) {
      const t = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toastMessage]);

  // Reset edit mode when post changes
  useEffect(() => {
    setEditMode('pan');
    resetElementEditing();
    setDeletedElementIds([]);
  }, [post.id]);

  // Reset editable content when post changes
  useEffect(() => {
    setEditedContent(post.content);
  }, [post.content]);

  // Reset edited dynamic texts when the post changes
  useEffect(() => {
    setEditedDynamicTexts({
      headline: post.dynamicTexts?.headline || '',
      tagline: post.dynamicTexts?.tagline || '',
      cta: post.dynamicTexts?.cta || '',
      quote: post.dynamicTexts?.quote || '',
      stat: post.dynamicTexts?.stat || '',
    });
    setHasDynamicTextChanges(false);
  }, [post.id, post.dynamicTexts]);

  // Reset state when template changes
  useEffect(() => {
    setSelectedTemplateId(initialTemplateId);
    setReview(null);
    setReviewError(null);
    setShowReviewPanel(false);
  }, [initialTemplateId]);

  // Fetch template when selection changes
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!selectedTemplateId) {
        setCurrentTemplate(null);
        setModifiedElements([]);
        return;
      }

      setIsLoadingTemplate(true);
      try {
        const data = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${selectedTemplateId}`);
        setCurrentTemplate(data);
        setModifiedElements(data.elements);
        setHasElementChanges(false);
        // Brand color: explicit if template.brandColor is set, else heuristic
        const explicit = data.brandColor && /^#[0-9a-fA-F]{6}$/.test(data.brandColor);
        if (explicit) {
          const norm = data.brandColor!.toLowerCase();
          setIsExplicitBrand(true);
          setThemeColor(norm);
          setOriginalThemeColor(norm);
        } else {
          const accent = detectAccentColor(data.elements);
          setIsExplicitBrand(false);
          setThemeColor(accent);
          setOriginalThemeColor(accent);
        }
        setBrandColorDirty(false);
      } catch (error) {
        console.error('Failed to fetch template:', error);
      } finally {
        setIsLoadingTemplate(false);
      }
    };
    fetchTemplate();
  }, [selectedTemplateId, workspaceId]);

  // Get dynamic text for element — reads from local editedDynamicTexts so
  // user edits show live in the canvas before saving.
  const getDynamicTextForElement = useCallback((dynamicType: string | undefined): string => {
    if (!dynamicType) return '';
    switch (dynamicType) {
      case 'headline': return editedDynamicTexts.headline || '';
      case 'tagline': return editedDynamicTexts.tagline || '';
      case 'cta': return editedDynamicTexts.cta || '';
      case 'quote': return editedDynamicTexts.quote || '';
      case 'stat': return editedDynamicTexts.stat || '';
      case 'custom': return editedDynamicTexts.headline || '';
      default: return '';
    }
  }, [editedDynamicTexts]);

  // Build dynamic texts map for rendering. Always walks the elements so that
  // user edits in the Properties panel (which live in editedDynamicTexts) get
  // applied even when the post never had AI-generated dynamicTexts to start
  // with (e.g. AI failed during generation).
  const buildDynamicTextsMap = useCallback((template: Template): Record<string, string> => {
    const textsMap: Record<string, string> = {};
    for (const element of template.elements) {
      if (element.elementType === 'text') {
        const textElement = element as TextElement;
        if (textElement.isDynamic && textElement.dynamicType) {
          const dynamicText = getDynamicTextForElement(textElement.dynamicType);
          if (dynamicText) {
            textsMap[element.id] = dynamicText;
          }
        }
      }
    }
    return textsMap;
  }, [getDynamicTextForElement]);

  // Render canvas with template
  const renderCanvas = useCallback(async () => {
    if (!canvasRef.current || !post.imageUrl || !currentTemplate) return;

    setIsRenderingCanvas(true);
    try {
      // Use modified elements if in edit mode
      const elementsToRender = hasElementChanges ? modifiedElements : currentTemplate.elements;
      const templateToRender = { ...currentTemplate, elements: elementsToRender };

      const dynamicTexts = buildDynamicTextsMap(templateToRender);
      const targetAspectRatio = PREVIEW_FORMATS[previewFormat].aspectRatio;

      const croppedCanvas = await renderCompositeAtAspectRatio(
        post.imageUrl,
        templateToRender,
        targetAspectRatio,
        {
          dynamicTexts,
          backgroundOffsetX: dragOffset.x,
          backgroundOffsetY: dragOffset.y,
          backgroundScale: bgScale,
          workspaceLogoUrl,
          logoScale,
        }
      );

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;

      canvasRef.current.width = croppedCanvas.width;
      canvasRef.current.height = croppedCanvas.height;
      ctx.drawImage(croppedCanvas, 0, 0);
    } catch (err) {
      console.error('Failed to render composite:', err);
    } finally {
      setIsRenderingCanvas(false);
    }
  }, [post.imageUrl, currentTemplate, previewFormat, dragOffset, bgScale, hasElementChanges, modifiedElements, buildDynamicTextsMap]);

  // Re-render when dependencies change
  useEffect(() => {
    if (isOpen && post.imageUrl) {
      renderCanvas();
    }
  }, [isOpen, post.imageUrl, currentTemplate, previewFormat, dragOffset, bgScale, hasElementChanges, modifiedElements, editedDynamicTexts, renderCanvas]);

  // ============== Action Handlers ==============

  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    onTemplateChange(post.id, templateId);
  }, [post.id, onTemplateChange]);

  const handleDownload = useCallback(() => {
    if (!canvasRef.current) return;

    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${workspaceName || 'post'}-branded.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [workspaceName]);

  const handleSaveToGallery = useCallback(async () => {
    if (!post.imageUrl) return;

    setIsSavingToGallery(true);
    try {
      const galleryItem: CreateGalleryItemRequest = {
        imageUrl: post.imageUrl,
        content: post.content,
        network: post.network,
        contentCategory: post.contentCategory || undefined,
        imageSource: post.imageSource || undefined,
        templateId: selectedTemplateId || undefined,
        format: previewFormat,
        backgroundOffsetX: dragOffset.x,
        backgroundOffsetY: dragOffset.y,
        backgroundScale: bgScale,
        dynamicTexts: post.dynamicTexts,
      };

      await apiClient.post<GalleryItem>(`/api/workspaces/${workspaceId}/gallery`, galleryItem);
      setSavedToGallery(true);
      onSaveToGallery?.();

      // Reset after 2 seconds
      setTimeout(() => setSavedToGallery(false), 2000);
    } catch (error) {
      console.error('Failed to save to gallery:', error);
    } finally {
      setIsSavingToGallery(false);
    }
  }, [post, selectedTemplateId, previewFormat, dragOffset, workspaceId, onSaveToGallery]);

  const handleSaveAsNewTemplate = useCallback(async () => {
    if (!currentTemplate || !hasElementChanges) return;

    setIsSavingTemplate(true);
    try {
      // 1. Create new template (copy of current with all properties)
      const newTemplate = await apiClient.post<Template>(`/api/workspaces/${workspaceId}/templates`, {
        name: `${currentTemplate.name} (Modified)`,
        canvasWidth: currentTemplate.canvasWidth,
        canvasHeight: currentTemplate.canvasHeight,
        backgroundColor: currentTemplate.backgroundColor,
        suitableFor: currentTemplate.suitableFor,
        layoutType: currentTemplate.layoutType,
        colorMode: currentTemplate.colorMode || 'fixed',
      });

      // 2. Create elements with all properties preserved
      for (const element of modifiedElements) {
        const elementData: Record<string, unknown> = {
          elementType: element.elementType,
          positionX: element.transform.positionX,
          positionY: element.transform.positionY,
          width: element.transform.width,
          height: element.transform.height,
          rotation: element.transform.rotation,
          flipHorizontal: !!element.transform.flipHorizontal,
          flipVertical: !!element.transform.flipVertical,
          opacity: element.opacity,
          zIndex: element.zIndex,
          name: element.name,
          usesBrandColor: element.usesBrandColor,
        };

        if (element.elementType === 'image') {
          const imgEl = element as ImageElement;
          Object.assign(elementData, {
            assetId: imgEl.assetId,
            imageUrl: imgEl.imageUrl,
            assetReferenceType: imgEl.assetReferenceType,
            clipShape: imgEl.clipShape,
            clipSkewAngle: imgEl.clipSkewAngle,
            clipPolygonPoints: imgEl.clipPolygonPoints,
          });
        } else if (element.elementType === 'text') {
          const txtEl = element as TextElement;
          Object.assign(elementData, {
            textContent: txtEl.textContent,
            fontFamily: txtEl.fontFamily,
            fontSize: txtEl.fontSize,
            fontWeight: txtEl.fontWeight,
            textColor: txtEl.textColor,
            textAlign: txtEl.textAlign,
            isDynamic: txtEl.isDynamic,
            dynamicType: txtEl.dynamicType,
            dynamicPrompt: txtEl.dynamicPrompt,
            autoSize: txtEl.autoSize,
            outlineColor: txtEl.outlineColor,
            outlineWidth: txtEl.outlineWidth,
            wrapStyle: txtEl.wrapStyle,
            letterSpacing: txtEl.letterSpacing,
            lineHeight: txtEl.lineHeight,
            textShadowEnabled: txtEl.textShadowEnabled,
          });
        } else if (element.elementType === 'shape') {
          const shpEl = element as ShapeElement;
          Object.assign(elementData, {
            backgroundColor: shpEl.backgroundColor,
            borderRadius: shpEl.borderRadius,
            shapeType: shpEl.shapeType,
            skewAngle: shpEl.skewAngle,
            polygonPoints: shpEl.polygonPoints,
            gradientType: shpEl.gradientType,
            gradientColors: shpEl.gradientColors,
            gradientAngle: shpEl.gradientAngle,
            shadowColor: shpEl.shadowColor,
            shadowBlur: shpEl.shadowBlur,
            shadowOffsetX: shpEl.shadowOffsetX,
            shadowOffsetY: shpEl.shadowOffsetY,
            borderColor: shpEl.borderColor,
            borderWidth: shpEl.borderWidth,
          });
        } else if (element.elementType === 'icon') {
          const icoEl = element as IconElement;
          Object.assign(elementData, {
            iconName: icoEl.iconName,
            iconColor: icoEl.iconColor,
          });
        }

        await apiClient.post(`/api/workspaces/${workspaceId}/templates/${newTemplate.id}/elements`, elementData);
      }

      // 3. Fetch complete template with elements
      const completeTemplate = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${newTemplate.id}`);

      // 4. Notify parent, link the post to the new template, reset state
      onTemplateCreated?.(completeTemplate);
      setCurrentTemplate(completeTemplate);
      setSelectedTemplateId(completeTemplate.id);
      onTemplateChange(post.id, completeTemplate.id);
      setModifiedElements(completeTemplate.elements);
      setHasElementChanges(false);
      setDeletedElementIds([]);

      alert(`Template "${completeTemplate.name}" created successfully!`);
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to create template. Please try again.');
    } finally {
      setIsSavingTemplate(false);
    }
  }, [currentTemplate, hasElementChanges, modifiedElements, workspaceId, post.id, onTemplateCreated, onTemplateChange]);

  // ============== Element property editing ==============

  // IDs of elements the user marked for deletion. Applied on Save Changes
  // (DELETE call) or Save as New Template (excluded from copy).
  const [deletedElementIds, setDeletedElementIds] = useState<string[]>([]);

  const deleteSelectedElement = useCallback(() => {
    if (!selectedElementId) return;
    setDeletedElementIds((prev) => [...prev, selectedElementId]);
    setModifiedElements((prev) => prev.filter((el) => el.id !== selectedElementId));
    setHasElementChanges(true);
    setSelectedElementId(null);
  }, [selectedElementId, setModifiedElements, setHasElementChanges, setSelectedElementId]);

  const updateSelectedElement = useCallback(
    (patch: Partial<TemplateElement>) => {
      if (!selectedElementId) return;
      setModifiedElements((prev) =>
        prev.map((el) =>
          el.id === selectedElementId ? ({ ...el, ...patch } as TemplateElement) : el
        )
      );
      setHasElementChanges(true);
    },
    [selectedElementId, setModifiedElements, setHasElementChanges]
  );

  /** Swap the template's theme/accent color.
   *  - EXPLICIT mode (template.brandColor set): swap only on flagged elements
   *    + mark brandColor as dirty so Save Changes also PUTs the template.
   *  - HEURISTIC mode: swap matching color on every element. */
  const handleThemeColorChange = useCallback(
    (newColor: string) => {
      if (!themeColor) return;
      const norm = newColor.toLowerCase();
      if (norm === themeColor) return;
      setModifiedElements((prev) =>
        swapColorInElements(prev, themeColor, norm, { onlyFlagged: isExplicitBrand })
      );
      setThemeColor(norm);
      setHasElementChanges(true);
      if (isExplicitBrand) setBrandColorDirty(true);
    },
    [themeColor, isExplicitBrand, setModifiedElements, setHasElementChanges]
  );

  const updateSelectedTransform = useCallback(
    (patch: Partial<ElementTransform>) => {
      if (!selectedElementId) return;
      setModifiedElements((prev) =>
        prev.map((el) =>
          el.id === selectedElementId
            ? { ...el, transform: { ...el.transform, ...patch } }
            : el
        )
      );
      setHasElementChanges(true);
    },
    [selectedElementId, setModifiedElements, setHasElementChanges]
  );

  /** Z-index reordering for the selected element. 'front' / 'back' jump to
   *  max±1; 'forward' / 'backward' move one step relative to current value. */
  const reorderSelected = useCallback(
    (op: 'front' | 'back' | 'forward' | 'backward') => {
      if (!selectedElementId) return;
      setModifiedElements((prev) => {
        const target = prev.find((el) => el.id === selectedElementId);
        if (!target) return prev;
        const all = prev.map((e) => e.zIndex);
        const max = Math.max(...all);
        const min = Math.min(...all);
        let newZ = target.zIndex;
        if (op === 'front') newZ = max + 1;
        else if (op === 'back') newZ = min - 1;
        else if (op === 'forward') newZ = target.zIndex + 1;
        else if (op === 'backward') newZ = target.zIndex - 1;
        if (newZ === target.zIndex) return prev;
        return prev.map((el) => (el.id === selectedElementId ? { ...el, zIndex: newZ } : el));
      });
      setHasElementChanges(true);
    },
    [selectedElementId, setModifiedElements, setHasElementChanges]
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);

  // Save modified elements back to the existing template (in-place update).
  // Skips elements that didn't change. Global templates can't be modified —
  // user must use "Save as New Template" instead (API returns 403).
  const handleSaveToCurrentTemplate = useCallback(async () => {
    if (!currentTemplate) return;
    if (!hasElementChanges && !hasDynamicTextChanges) return;

    // Non-admins cannot modify global templates (server enforces this too).
    if (hasElementChanges && currentTemplate.isGlobal && !isAdmin) {
      alert('Global templates can\'t be modified directly. Use "Save as New Template" to create your own copy.');
      return;
    }

    setIsSavingChanges(true);
    try {
      // Delete removed elements first
      for (const deletedId of deletedElementIds) {
        await apiClient.delete<unknown>(
          `/api/workspaces/${workspaceId}/templates/${currentTemplate.id}/elements/${deletedId}`
        );
      }

      const original = new Map(currentTemplate.elements.map((e) => [e.id, e]));
      const changed = modifiedElements.filter((el) => {
        const orig = original.get(el.id);
        return !orig || JSON.stringify(orig) !== JSON.stringify(el);
      });

      for (const element of changed) {
        const elementData: Record<string, unknown> = {
          positionX: element.transform.positionX,
          positionY: element.transform.positionY,
          width: element.transform.width,
          height: element.transform.height,
          rotation: element.transform.rotation,
          flipHorizontal: !!element.transform.flipHorizontal,
          flipVertical: !!element.transform.flipVertical,
          opacity: element.opacity,
          zIndex: element.zIndex,
          name: element.name,
          usesBrandColor: element.usesBrandColor,
        };

        if (element.elementType === 'text') {
          const t = element as TextElement;
          Object.assign(elementData, {
            textContent: t.textContent,
            fontFamily: t.fontFamily,
            fontSize: t.fontSize,
            fontWeight: t.fontWeight,
            textColor: t.textColor,
            textAlign: t.textAlign,
            autoSize: t.autoSize,
            wrapStyle: t.wrapStyle,
            outlineColor: t.outlineColor,
            outlineWidth: t.outlineWidth,
            letterSpacing: t.letterSpacing,
            lineHeight: t.lineHeight,
            textShadowEnabled: t.textShadowEnabled,
            isDynamic: t.isDynamic,
            dynamicType: t.dynamicType,
            dynamicPrompt: t.dynamicPrompt,
          });
        } else if (element.elementType === 'shape') {
          const s = element as ShapeElement;
          Object.assign(elementData, {
            backgroundColor: s.backgroundColor,
            borderRadius: s.borderRadius,
            shapeType: s.shapeType,
            skewAngle: s.skewAngle,
            polygonPoints: s.polygonPoints,
            gradientType: s.gradientType,
            gradientColors: s.gradientColors,
            gradientAngle: s.gradientAngle,
            shadowColor: s.shadowColor,
            shadowBlur: s.shadowBlur,
            shadowOffsetX: s.shadowOffsetX,
            shadowOffsetY: s.shadowOffsetY,
            borderColor: s.borderColor,
            borderWidth: s.borderWidth,
          });
        } else if (element.elementType === 'image') {
          const i = element as ImageElement;
          Object.assign(elementData, {
            assetId: i.assetId,
            imageUrl: i.imageUrl,
            assetReferenceType: i.assetReferenceType,
            clipShape: i.clipShape,
            clipSkewAngle: i.clipSkewAngle,
            clipPolygonPoints: i.clipPolygonPoints,
          });
        } else if (element.elementType === 'icon') {
          const ico = element as IconElement;
          Object.assign(elementData, {
            iconName: ico.iconName,
            iconColor: ico.iconColor,
          });
        }

        await apiClient.put(
          `/api/workspaces/${workspaceId}/templates/${currentTemplate.id}/elements/${element.id}`,
          elementData
        );
      }

      // Save edited dynamic texts to the post
      let dynamicTextOps = 0;
      if (hasDynamicTextChanges) {
        await apiClient.put(`/api/workspaces/${workspaceId}/posts/${post.id}`, {
          dynamicTexts: editedDynamicTexts,
        });
        dynamicTextOps = 1;
        setHasDynamicTextChanges(false);
      }

      // Persist template-level brand color when it changed (explicit mode).
      if (brandColorDirty && themeColor && isExplicitBrand) {
        await apiClient.put(`/api/workspaces/${workspaceId}/templates/${currentTemplate.id}`, {
          brandColor: themeColor,
        });
        setBrandColorDirty(false);
      }

      const refreshed = currentTemplate.id && (hasElementChanges || deletedElementIds.length > 0)
        ? await apiClient.get<Template>(
            `/api/workspaces/${workspaceId}/templates/${currentTemplate.id}`
          )
        : currentTemplate;
      setCurrentTemplate(refreshed);
      setModifiedElements(refreshed.elements);
      setHasElementChanges(false);
      const totalOps = changed.length + deletedElementIds.length + dynamicTextOps;
      const deletedCount = deletedElementIds.length;
      setDeletedElementIds([]);
      setToastMessage(
        deletedCount > 0
          ? `Saved ${totalOps} change${totalOps === 1 ? '' : 's'} (${deletedCount} deleted)`
          : `Saved ${totalOps} change${totalOps === 1 ? '' : 's'}`
      );
    } catch (err) {
      console.error('Failed to save template changes:', err);
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      alert(message);
    } finally {
      setIsSavingChanges(false);
    }
  }, [currentTemplate, hasElementChanges, hasDynamicTextChanges, brandColorDirty, themeColor, isExplicitBrand, modifiedElements, deletedElementIds, editedDynamicTexts, workspaceId, post.id, isAdmin, setModifiedElements, setHasElementChanges]);

  const handleSaveContent = useCallback(async () => {
    if (editedContent === post.content || isSavingContent) return;

    setIsSavingContent(true);
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/posts/${post.id}`, { content: editedContent });
      onContentSaved?.(post.id, editedContent);
    } catch (error) {
      console.error('Failed to save content:', error);
    } finally {
      setIsSavingContent(false);
    }
  }, [editedContent, post.content, post.id, workspaceId, isSavingContent, onContentSaved]);

  const [isSavingStyled, setIsSavingStyled] = useState(false);

  const handleSaveStyledVersion = useCallback(async () => {
    if (!selectedTemplateId || !workspaceId || !currentTemplate) return;

    setIsSavingStyled(true);
    try {
      // Clone the template via duplicate API
      const cloned = await apiClient.post<TemplateSummary>(
        `/api/workspaces/${workspaceId}/templates/${selectedTemplateId}/duplicate`,
        {}
      );
      // Update the clone to have fixed color mode with a descriptive name
      await apiClient.put(`/api/workspaces/${workspaceId}/templates/${cloned.id}`, {
        name: `${currentTemplate.name || 'Template'} (Brand Styled)`,
        colorMode: 'fixed',
      });
      // Fetch the complete updated template
      const completeTemplate = await apiClient.get<Template>(
        `/api/workspaces/${workspaceId}/templates/${cloned.id}`
      );
      onTemplateCreated?.(completeTemplate);
      alert(`Styled template "${completeTemplate.name}" created successfully!`);
    } catch (err) {
      console.error('Failed to save styled version:', err);
      alert('Failed to save styled version. Please try again.');
    } finally {
      setIsSavingStyled(false);
    }
  }, [selectedTemplateId, workspaceId, currentTemplate, onTemplateCreated]);

  const handleClose = useCallback(() => {
    if (hasElementChanges) {
      const confirm = window.confirm('You have unsaved element changes. Discard them?');
      if (!confirm) return;
    }
    setEditMode('pan');
    setSelectedElementId(null);
    setHasElementChanges(false);
    onClose();
  }, [hasElementChanges, onClose]);

  // ============== AI Review Handlers ==============

  const handleRequestReview = useCallback(async () => {
    if (!canvasRef.current || !currentTemplate) return;
    setIsReviewing(true);
    setShowReviewPanel(true);
    try {
      // Scale down canvas for smaller payload
      const reviewCanvas = document.createElement('canvas');
      const scale = 0.5;
      reviewCanvas.width = canvasRef.current.width * scale;
      reviewCanvas.height = canvasRef.current.height * scale;
      const ctx = reviewCanvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(canvasRef.current, 0, 0, reviewCanvas.width, reviewCanvas.height);
      const dataUrl = reviewCanvas.toDataURL('image/png');

      const metadata: ReviewMetadata = {
        templateId: currentTemplate.id,
        templateName: currentTemplate.name,
        layoutType: currentTemplate.layoutType || 'generic',
        canvasWidth: currentTemplate.canvasWidth,
        canvasHeight: currentTemplate.canvasHeight,
        elementCount: currentTemplate.elements.length,
        dynamicTextTypes: currentTemplate.elements
          .filter((e) => e.elementType === 'text' && (e as TextElement).isDynamic)
          .map((e) => (e as TextElement).dynamicType || 'custom'),
        imageSource: post.imageSource,
        brandColors,
        brandFonts,
      };

      const result = await reviewApi.createReview(workspaceId, post.id, {
        imageBase64: dataUrl,
        metadata,
      });

      // Enrich issues with auto-fix proposals
      const enrichedIssues = enrichIssuesWithFixes(result.review.issues, modifiedElements);
      setReview({ ...result.review, issues: enrichedIssues });
      setReviewError(null);
      setToastMessage(`Review complete! Score: ${result.review.score}/100`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Review analysis failed';
      console.error('Review failed:', error);
      setReviewError(msg);
    } finally {
      setIsReviewing(false);
    }
  }, [canvasRef, currentTemplate, post, workspaceId, modifiedElements]);

  const handleApplyFix = useCallback((_issueIndex: number, fix: ReviewProposedFix) => {
    if (!fix.elementId) return;

    const validatedFix = validateProposedFix(fix);
    if (!validatedFix) {
      setToastMessage('Fix contains invalid values');
      return;
    }

    setModifiedElements((prev) =>
      prev.map((el) => {
        if (el.id !== validatedFix.elementId) return el;

        const updated = { ...el };
        if (validatedFix.transform) {
          updated.transform = { ...el.transform, ...validatedFix.transform };
        }
        if (validatedFix.opacity !== undefined) {
          updated.opacity = validatedFix.opacity;
        }
        if (el.elementType === 'text') {
          const textEl = { ...updated } as TextElement;
          if (validatedFix.textColor) textEl.textColor = validatedFix.textColor;
          if (validatedFix.fontSize) textEl.fontSize = validatedFix.fontSize;
          if (validatedFix.outlineColor) textEl.outlineColor = validatedFix.outlineColor;
          if (validatedFix.outlineWidth !== undefined) textEl.outlineWidth = validatedFix.outlineWidth;
          if (validatedFix.fontWeight) textEl.fontWeight = validatedFix.fontWeight as TextElement['fontWeight'];
          if (validatedFix.fontFamily) textEl.fontFamily = validatedFix.fontFamily;
          if (validatedFix.textAlign) textEl.textAlign = validatedFix.textAlign as TextElement['textAlign'];
          if (validatedFix.letterSpacing !== undefined) textEl.letterSpacing = validatedFix.letterSpacing;
          if (validatedFix.lineHeight !== undefined) textEl.lineHeight = validatedFix.lineHeight;
          return textEl as unknown as TemplateElement;
        }
        if (el.elementType === 'shape') {
          const shapeEl = { ...updated } as ShapeElement;
          if (validatedFix.backgroundColor) shapeEl.backgroundColor = validatedFix.backgroundColor;
          return shapeEl as unknown as TemplateElement;
        }
        return updated;
      })
    );
    setHasElementChanges(true);
    setToastMessage('Fix applied');
  }, []);

  const handleApplyAllFixes = useCallback(() => {
    if (!review) return;
    review.issues.forEach((issue, index) => {
      if (issue.proposedFix) {
        handleApplyFix(index, issue.proposedFix);
      }
    });
    const fixCount = review.issues.filter(i => i.proposedFix).length;
    setToastMessage(`${fixCount} fixes applied`);
  }, [review, handleApplyFix]);

  // ============== Render ==============

  if (!isOpen) return null;

  const imageUrl = post.imageUrl;
  const isPastel = imageUrl ? isPastelUri(imageUrl) : false;
  const pastelColor = isPastel && imageUrl ? parsePastelUri(imageUrl) : null;

  return (
    <Overlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={handleClose}>&times;</CloseBtn>

        {/* ========== LEFT PANEL: Toolbar ========== */}
        <LeftPanel>
          {/* Pan / Elements mode toggle */}
          {selectedTemplateId && currentTemplate && currentTemplate.elements.length > 0 && (
            <>
              <PanelLabel>Mode</PanelLabel>
              <ModeToggle>
                <ModeButton $active={editMode === 'pan'} onClick={() => setEditMode('pan')}>
                  Pan
                </ModeButton>
                <ModeButton $active={editMode === 'elements'} onClick={() => setEditMode('elements')}>
                  Edit Mode
                </ModeButton>
              </ModeToggle>
            </>
          )}

          {/* Format selector */}
          <PanelLabel>Format</PanelLabel>
          <FormatSelector value={previewFormat} onChange={onPreviewFormatChange} />

          {/* Template selector */}
          <PanelLabel>Template</PanelLabel>
          <TemplateSelect value={selectedTemplateId} onChange={(e) => handleTemplateSelect(e.target.value)}>
            <option value="">No Template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.elementCount} el.)
              </option>
            ))}
          </TemplateSelect>

          {/* Zoom controls (only in pan mode with template) */}
          {editMode === 'pan' && selectedTemplateId && !isPastel && (
            <>
              <PanelLabel>Zoom</PanelLabel>
              <ZoomControls>
                <ZoomButton onClick={handleScaleDown}>-</ZoomButton>
                <ZoomLabel onClick={handleScaleReset}>{Math.round(bgScale * 100)}%</ZoomLabel>
                <ZoomButton onClick={handleScaleUp}>+</ZoomButton>
              </ZoomControls>

              <OffsetIndicator>
                Pan: {dragOffset.x.toFixed(0)}%, {dragOffset.y.toFixed(0)}%
              </OffsetIndicator>

              {hasUnsavedChanges && (
                <Button $size="sm" $fullWidth onClick={handleSavePosition}>
                  Save Position
                </Button>
              )}

              {(dragOffset.x !== 0 || dragOffset.y !== 0 || bgScale !== 1.0) && (
                <Button $size="sm" $variant="outline" $fullWidth onClick={handleResetOffset}>
                  Reset
                </Button>
              )}
            </>
          )}

          {/* Theme color — swap the template's accent color in one click */}
          {themeColor && currentTemplate && (
            <>
              <PanelLabel>
                Theme Color {isExplicitBrand ? '(Brand)' : '(Auto)'}
              </PanelLabel>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="color"
                  value={themeColor}
                  onChange={(e) => handleThemeColorChange(e.target.value)}
                  style={{
                    width: 40,
                    height: 32,
                    padding: 0,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  title="Replaces this color everywhere in the template"
                />
                <input
                  type="text"
                  value={themeColor}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) handleThemeColorChange(v.toLowerCase());
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '6px 8px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontSize: 11,
                    fontFamily: 'monospace',
                  }}
                />
              </div>
              {originalThemeColor && themeColor !== originalThemeColor && (
                <Button
                  $size="sm"
                  $variant="ghost"
                  $fullWidth
                  onClick={() => handleThemeColorChange(originalThemeColor)}
                >
                  Reset color
                </Button>
              )}
            </>
          )}

          {/* Save changes — element edits go to template, dynamic text edits go to post.
              Element-edit button hidden on global templates for non-admins (API would 403);
              dynamic-text edits always allowed (they target the post, not the template). */}
          {(hasElementChanges || hasDynamicTextChanges) && currentTemplate && (
            (!currentTemplate.isGlobal || isAdmin || (hasDynamicTextChanges && !hasElementChanges)) && (
              <Button
                $size="sm"
                $fullWidth
                onClick={handleSaveToCurrentTemplate}
                disabled={isSavingChanges}
              >
                {isSavingChanges
                  ? 'Saving...'
                  : !hasElementChanges && hasDynamicTextChanges
                    ? 'Save Text Changes'
                    : currentTemplate.isGlobal
                      ? 'Save Changes (Global)'
                      : 'Save Changes'}
              </Button>
            )
          )}

          {/* Hint for non-admins on global templates trying to edit elements */}
          {hasElementChanges && currentTemplate?.isGlobal && !isAdmin && (
            <OffsetIndicator style={{ fontSize: 10, lineHeight: 1.3 }}>
              Global templates can&apos;t have their elements edited directly. Use &ldquo;Save as New Template&rdquo; to make your own copy. (AI text edits save to the post and work on globals too.)
            </OffsetIndicator>
          )}

          {/* Save as new template (always available — required for global templates) */}
          {hasElementChanges && (
            <Button
              $size="sm"
              $variant="outline"
              $fullWidth
              onClick={handleSaveAsNewTemplate}
              disabled={isSavingTemplate}
            >
              {isSavingTemplate ? 'Saving...' : 'Save as New Template'}
            </Button>
          )}

          {/* Save styled version (when using brand/adaptive color mode) */}
          {currentTemplate && (currentTemplate.colorMode === 'brand' || currentTemplate.colorMode === 'adaptive') && (
            <Button $size="sm" $variant="outline" $fullWidth onClick={handleSaveStyledVersion} disabled={isSavingStyled}>
              {isSavingStyled ? 'Saving...' : 'Save Styled Version'}
            </Button>
          )}

          <Divider />

          {/* Save to Gallery */}
          <Button
            $size="sm"
            $fullWidth
            $variant={savedToGallery ? 'primary' : 'outline'}
            onClick={handleSaveToGallery}
            disabled={isSavingToGallery || savedToGallery}
          >
            {savedToGallery ? 'Saved!' : isSavingToGallery ? 'Saving...' : 'Save to Gallery'}
          </Button>

          {/* AI Review */}
          {reviewCapability && selectedTemplateId && currentTemplate && (
            reviewCapability.tier !== 'none' ? (
              <Button
                $size="sm"
                $fullWidth
                $variant={review ? 'primary' : 'outline'}
                onClick={review ? () => setShowReviewPanel(!showReviewPanel) : handleRequestReview}
                disabled={isReviewing || !currentTemplate}
              >
                {isReviewing ? 'Analyzing...' : review ? `Score: ${review.score}` : 'AI Review'}
              </Button>
            ) : (
              <Button
                $size="sm"
                $fullWidth
                $variant="outline"
                onClick={onNavigateBilling}
                style={{ opacity: 0.7 }}
              >
                AI Review (Pro)
              </Button>
            )
          )}

          {/* Download */}
          {selectedTemplateId && (
            <Button $size="sm" $fullWidth onClick={handleDownload} disabled={isLoadingTemplate || isRenderingCanvas}>
              {isLoadingTemplate || isRenderingCanvas ? 'Loading...' : 'Download'}
            </Button>
          )}

          {/* Spacer pushes close to bottom */}
          <div style={{ flex: 1 }} />

          <Button $size="sm" $variant="ghost" $fullWidth onClick={handleClose}>
            Close
          </Button>
        </LeftPanel>

        {/* ========== CENTER PANEL: Canvas ========== */}
        <CenterPanel>
          <CanvasContainer
            $isDragging={isDragging && editMode === 'pan'}
            onMouseDown={selectedTemplateId && !isPastel && editMode === 'pan' ? handlePanStart : undefined}
            onMouseMove={editMode === 'pan' ? handlePanMove : handleElementMouseMove}
            onMouseUp={editMode === 'pan' ? handlePanEnd : handleElementMouseUp}
            onMouseLeave={editMode === 'pan' ? handlePanEnd : handleElementMouseUp}
            onTouchStart={selectedTemplateId && !isPastel && editMode === 'pan' ? handlePanStart : undefined}
            onTouchMove={handlePanMove}
            onTouchEnd={handlePanEnd}
            onWheel={handleWheel}
          >
            {selectedTemplateId && currentTemplate ? (
              <>
                <CanvasPreview ref={canvasRef} style={{ maxHeight: 'calc(90vh - 32px)', width: 'auto' }} />

                {/* Elements overlay for editing mode */}
                {editMode === 'elements' && canvasRef.current && (
                  <ElementsOverlay>
                    {modifiedElements.map((element) => (
                      <ElementWrapper
                        key={element.id}
                        $x={element.transform.positionX}
                        $y={element.transform.positionY}
                        $width={element.transform.width}
                        $height={element.transform.height}
                        $rotation={element.transform.rotation}
                        $flipH={!!element.transform.flipHorizontal}
                        $flipV={!!element.transform.flipVertical}
                        $opacity={1}
                        $selected={selectedElementId === element.id}
                        $zIndex={element.zIndex + 1000}
                        onMouseDown={(e) => handleElementMouseDown(e, element)}
                      >
                        <ElementPreview>
                          {element.elementType === 'text'
                            ? (element as TextElement).isDynamic
                              ? `[${(element as TextElement).dynamicType}]`
                              : (element as TextElement).textContent?.substring(0, 20) || 'Text'
                            : 'Image'}
                        </ElementPreview>

                        {selectedElementId === element.id && (
                          <>
                            <ResizeHandle $position="nw" onMouseDown={(e) => handleResizeMouseDown(e, element, 'nw')} />
                            <ResizeHandle $position="ne" onMouseDown={(e) => handleResizeMouseDown(e, element, 'ne')} />
                            <ResizeHandle $position="sw" onMouseDown={(e) => handleResizeMouseDown(e, element, 'sw')} />
                            <ResizeHandle $position="se" onMouseDown={(e) => handleResizeMouseDown(e, element, 'se')} />
                            <RotateHandle
                              onMouseDown={(e) => handleRotateMouseDown(e, element)}
                              title="Drag to rotate (Shift = snap to 15°)"
                            />
                          </>
                        )}
                      </ElementWrapper>
                    ))}
                  </ElementsOverlay>
                )}

                {(isLoadingTemplate || isRenderingCanvas) && (
                  <LoadingOverlay>Loading preview...</LoadingOverlay>
                )}
              </>
            ) : isPastel && pastelColor ? (
              <PastelBackground
                $color={pastelColor}
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  aspectRatio: PREVIEW_FORMATS[previewFormat].aspectRatio,
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                }}
              />
            ) : imageUrl ? (
              <div
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  maxHeight: 'calc(90vh - 32px)',
                  aspectRatio: PREVIEW_FORMATS[previewFormat].aspectRatio,
                  border: '1px solid #E2E8F0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={imageUrl}
                  alt="Full size"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            ) : null}
          </CanvasContainer>

        </CenterPanel>

        {/* ========== RIGHT PANEL: Properties / Review Chat / Post Text ========== */}
        <RightPanel>
          {(() => {
            const selectedElement = editMode === 'elements' && selectedElementId
              ? modifiedElements.find((el) => el.id === selectedElementId)
              : null;
            return selectedElement;
          })() ? (
            <ElementPropertiesPanel
              element={modifiedElements.find((el) => el.id === selectedElementId)!}
              dynamicTexts={editedDynamicTexts}
              brandColor={isExplicitBrand ? themeColor : null}
              onUpdate={updateSelectedElement}
              onUpdateTransform={updateSelectedTransform}
              onUpdateDynamicText={(type, value) => {
                setEditedDynamicTexts((prev) => ({ ...prev, [type]: value }));
                setHasDynamicTextChanges(true);
              }}
              onRegenerateDynamicText={async (type) => {
                try {
                  const res = await apiClient.post<{ type: string; text: string }>(
                    `/api/workspaces/${workspaceId}/posts/${post.id}/regenerate-text`,
                    { type }
                  );
                  if (res?.text) {
                    setEditedDynamicTexts((prev) => ({ ...prev, [type]: res.text }));
                    // Server already persisted the new text to the post, so no
                    // dirty flag — just refresh the canvas.
                    setToastMessage(`Regenerated ${type}: "${res.text.length > 28 ? res.text.slice(0, 28) + '…' : res.text}"`);
                    return res.text;
                  }
                } catch (err) {
                  const message = err instanceof Error ? err.message : 'Regeneration failed';
                  setToastMessage(`Failed to regenerate: ${message}`);
                }
                return null;
              }}
              onReorder={reorderSelected}
              onDeselect={() => setSelectedElementId(null)}
              onDelete={deleteSelectedElement}
            />
          ) : showReviewPanel && reviewCapability && reviewCapability.tier !== 'none' ? (
            <NovahReviewChat
              review={review}
              isLoading={isReviewing}
              error={reviewError}
              reviewCapability={reviewCapability}
              onRequestReview={handleRequestReview}
              onApplyFix={reviewCapability.tier === 'review_and_fix' ? handleApplyFix : (_i, _f) => {}}
              onApplyAllFixes={reviewCapability.tier === 'review_and_fix' ? handleApplyAllFixes : () => {}}
              onRetry={() => { setReviewError(null); handleRequestReview(); }}
              onNavigateBilling={onNavigateBilling}
            />
          ) : (
            <>
              {/* Tags */}
              <SectionLabel>Info</SectionLabel>
              <TagsRow>
                <Tag>{post.network}</Tag>
                {post.contentCategory && <Tag>{post.contentCategory}</Tag>}
                {post.imageSource && <Tag>{post.imageSource}</Tag>}
              </TagsRow>

              {/* Editable post content */}
              <SectionLabel>Post Text</SectionLabel>
              <EditableText
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
              />
              <Button
                $size="sm"
                $fullWidth
                onClick={handleSaveContent}
                disabled={editedContent === post.content || isSavingContent}
              >
                {isSavingContent ? 'Saving...' : 'Save Text'}
              </Button>

              {/* Dynamic texts (read-only) */}
              {post.dynamicTexts && (
                <>
                  <SectionLabel>Dynamic Texts</SectionLabel>
                  {post.dynamicTexts.headline && (
                    <DynamicTextItem>
                      <DynamicTextLabel>Headline</DynamicTextLabel>
                      {post.dynamicTexts.headline}
                    </DynamicTextItem>
                  )}
                  {post.dynamicTexts.tagline && (
                    <DynamicTextItem>
                      <DynamicTextLabel>Tagline</DynamicTextLabel>
                      {post.dynamicTexts.tagline}
                    </DynamicTextItem>
                  )}
                  {post.dynamicTexts.cta && (
                    <DynamicTextItem>
                      <DynamicTextLabel>CTA</DynamicTextLabel>
                      {post.dynamicTexts.cta}
                    </DynamicTextItem>
                  )}
                  {post.dynamicTexts.quote && (
                    <DynamicTextItem>
                      <DynamicTextLabel>Quote</DynamicTextLabel>
                      {post.dynamicTexts.quote}
                    </DynamicTextItem>
                  )}
                </>
              )}
            </>
          )}
        </RightPanel>
      </ModalContent>

      {toastMessage && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: 'white', padding: '10px 20px', borderRadius: 8,
          fontSize: 13, fontWeight: 600, zIndex: 1100, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          {toastMessage}
        </div>
      )}
    </Overlay>
  );
}
